import {
  ArrowsHorizontal,
  CaretDown,
  Check,
  Equals,
  type IconProps,
  NotEquals,
  X,
} from '@phosphor-icons/react';
import {
  useRef,
  useState,
  useEffect,
  isValidElement,
  cloneElement,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import ReactDatePicker from 'react-datepicker';
import { Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';
import { HoverBorderButton, MuteText, StyledButton } from '../elements';
import { CommonUtility, DateFormat, DateUtility } from '../utility';
import { NumberMaskInput } from '../elements/CustomMaskInput';
import {
  CustomDateInput,
  DatepickerContainer,
  DateRangeInputContainer,
} from '../elements/Datepicker';
import { Flag } from '../elements/Flag';
import { useOutsideAlerter } from '../hooks/outside';

const SelectionContainer = styled.div`
  box-shadow: 0px 5px 25px rgba(57, 57, 72, 0.05);
  background: ${({ theme }) => theme.colors.white};
  min-width: 250px;
  border-radius: 5px;
  .pointer {
    cursor: pointer;
  }
`;

const FilterHoverButton = styled(HoverBorderButton)`
  position: relative;
  height: 40px !important;
`;

const FilterTitleButton = styled(HoverBorderButton)`
  position: relative;
  height: 30px !important;
  border-color: ${({ theme }) => theme.colors.grey} !important;
  &.light-btn {
    color: ${({ theme }) => theme.colors.white} !important;
  }
`;

const Border = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.box};
`;
export const filterTypes = {
  is: 'EQUAL',
  isNot: 'NOT_EQUAL',
};

const CancelButton = styled(X)`
  position: absolute;
  right: 8px;
  &:hover {
    color: ${({ theme }) => theme.colors.grey};
  }
`;

const filterOptions = [
  {
    key: filterTypes.is,
    value: filterTypes.is,
    text: 'Equals',
    conditionImage: <Equals size={8} weight="bold" />,
  },
  {
    key: filterTypes.isNot,
    value: filterTypes.isNot,
    text: 'Does Not Equal',
    conditionImage: <NotEquals height={8} />,
  },
];

interface IFilterOption {
  value: string | number;
  text: string;
  [key: string]: any;
}

export interface IFilterConfig {
  name: string;
  label?: string;
  type?: 'text' | 'date' | 'select';
  multiple?: boolean;
  defaultValue?: string | string[] | number;
  defaultQuery?: string;
  options?: IFilterOption[];
  skip?: boolean;
  must?: boolean;
  typeQuery?: string;
  removeOthers?: string[];
  [key: string]: any;
}

interface IUserValueItem {
  value: string | number | (string | number)[] | Date | [Date | null, Date | null];
  type: string;
  options?: IFilterOption[];
  filterType?: string;
  typeQuery?: string;
  removeOthers?: string[];
  toShow?: string | string[] | ReactElement;
  skip?: boolean;
  index?: number;
}

interface IUserValue {
  [key: string]: IUserValueItem;
}

interface IFilterButtonProps {
  filters: IFilterConfig[];
  loading?: boolean;
  setFinalFilter: (filter: Record<string, any>) => void;
  setFinalvalue: (value: IUserValue) => void;
  finalValue: IUserValue;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  titleBtn?: boolean;
  btnClassName?: string;
}

export function FilterButton({
  filters,
  loading,
  setFinalFilter,
  setFinalvalue,
  finalValue,
  activeIndex,
  setActiveIndex,
  open,
  setOpen,
  titleBtn,
  btnClassName = '',
}: IFilterButtonProps): JSX.Element {
  const [userValue, setUserValue] = useState<IUserValue>({});
  const [localFilter, setLocalFilter] = useState<string | null>(null);

  const applyFilter = (customValue: IUserValue | null = null): void => {
    const _userValue = customValue || userValue;
    let hasFilters = false;

    for (const filterKey in _userValue) {
      if (!_userValue[filterKey].skip) {
        if (_userValue[filterKey].value) {
          hasFilters = true;
          break;
        }
      }
    }

    if (hasFilters) {
      setFinalvalue(_userValue);
    }
  };

  useEffect(() => {
    try {
      setFinalFilter(localFilter ? JSON.parse(localFilter) : {});
    } catch (error) {
      // console.log('error', error);
    }
  }, [localFilter]);

  useEffect(() => {
    if (Object.keys(finalValue).length < 1) return;

    const _userValue = { ...finalValue };
    const finalFilter: Record<string, string> = {};

    Object.keys(_userValue).forEach(name => {
      if (_userValue[name].filterType === 'date') {
        if (Array.isArray(_userValue[name].value)) {
          finalFilter[name] = (_userValue[name].value as (Date | string)[])
            .map(x => DateUtility.formatDate(x, DateFormat.yearMonthDate))
            .join(',');
        } else {
          finalFilter[name] = DateUtility.formatDate(
            _userValue[name].value as Date | string,
            DateFormat.yearMonthDate,
          );
        }
      } else if (Array.isArray(_userValue[name].value)) {
        if ((_userValue[name].value as (string | number)[])[0] === '-1') {
          finalFilter[name] = '';
        } else if (
          _userValue[name].type === filterTypes.is ||
          _userValue[name].typeQuery
        ) {
          finalFilter[name] = (_userValue[name].value as (string | number)[]).join(',');
        } else {
          const notSelected = _userValue[name].options?.filter(
            x =>
              x.value !== -1 &&
              x.value &&
              !(_userValue[name].value as (string | number)[]).includes(x.value),
          );

          finalFilter[name] = notSelected?.map(x => x.value)?.join(',') || '';
        }
      } else if (_userValue[name].type === filterTypes.is || _userValue[name].typeQuery) {
        finalFilter[name] = String(_userValue[name].value);
      } else if (!_userValue[name].typeQuery) {
        const notSelected = _userValue[name].options?.filter(
          x => x.value !== -1 && x.value && _userValue[name].value !== x.value,
        );
        finalFilter[name] = notSelected?.map(x => x.value)?.join(',') || '';
      }
      if (_userValue[name].typeQuery) {
        finalFilter[_userValue[name].typeQuery!] = _userValue[name].type;
      }
    });

    setLocalFilter(finalFilter ? JSON.stringify(finalFilter) : null);
    setActiveIndex(-1);
    setOpen(false);
  }, [finalValue, setActiveIndex, setOpen]);

  useEffect(() => {
    setUserValue(finalValue);
  }, [finalValue]);

  useEffect(() => {
    if (open) setUserValue(finalValue);
  }, [open, finalValue]);

  useEffect(() => {
    const newUserValue: IUserValue = {};

    filters?.forEach((filter, indexOfFilter) => {
      let value: string | number | (string | number)[] | Date | [Date, Date] = '';
      let toShow: string | string[] = '';

      if (filter.multiple) {
        value = [];
      }

      if (filter.defaultValue !== undefined && filter.defaultValue !== null) {
        // preserve falsy numeric defaults like 0 and keep typesafe assignment
        value = filter.defaultValue as string | number | (string | number)[];

        if (filter.type === 'date') {
          const dates = Array.isArray(filter.defaultValue)
            ? (filter.defaultValue as string[])
            : String(filter.defaultValue).split(',');
          if (dates.length > 1) {
            value = [new Date(dates[0]), new Date(dates[1])];
          } else {
            value = new Date(String(filter.defaultValue));
          }
        } else if (filter.multiple) {
          const arr: string[] = [];
          const valueArray = Array.isArray(value) ? value : String(value).split(',');

          valueArray.forEach(dValue => {
            if (CommonUtility.isValidArray(filter?.options)) {
              const opt = filter.options!.find(x => x.value === dValue);
              if (opt) {
                arr.push(opt.text);
              }
            }
          });
          toShow = arr;
        } else if (filter.options && filter.options.length > 0) {
          const opt = filter.options.find(x => x.value === filter.defaultValue);
          toShow = opt?.text || '';
        } else {
          toShow = String(value);
        }
      }

      if (filter.skip) toShow = '';

      newUserValue[filter.name] = {
        value,
        type: filterTypes.is,
        options: filter.options,
        filterType: filter.type,
        typeQuery: filter.typeQuery,
        removeOthers: filter.removeOthers,
        toShow,
        skip: !!filter.skip,
        index: indexOfFilter,
      };

      if (filter.defaultQuery) {
        newUserValue[filter.name].type = filter.defaultQuery;
      }
    });

    setUserValue(newUserValue);
    setFinalvalue(newUserValue);
  }, [filters, setFinalvalue]);

  const closeDropdown = (): void => {
    closeSelection();
    setOpen(false);
  };

  const ref = useRef<HTMLDivElement>(null);
  useOutsideAlerter(ref, closeDropdown);

  const closeSelection = (): void => {
    setUserValue(finalValue);
    setActiveIndex(-1);
  };

  const openDropdown = (): void => {
    setOpen(true);
  };

  const updateFilter = (key: string, value: Partial<IUserValueItem>): void => {
    const data = {
      ...userValue,
      [key]: {
        ...userValue[key],
        ...value,
      },
    };
    setUserValue(data);
    setFinalvalue(data);
  };

  const updateMultiple = (name: string, obj: IUpdateMultipleParams): void => {
    setUserValue(prev => {
      const newPrev = { ...prev };

      if (CommonUtility.isValidArray(newPrev[name].removeOthers)) {
        newPrev[name].removeOthers!.forEach(key => {
          newPrev[key].value = '';
        });
      }

      return {
        ...newPrev,
        [name]: {
          ...newPrev[name],
          ...obj,
        },
      };
    });
  };

  const clearFilter = (item: IFilterConfig): void => {
    if (item.multiple) {
      updateFilter(item.name, { value: [], type: filterTypes.is });
    } else {
      updateFilter(item.name, { value: '', type: filterTypes.is });
    }
  };

  const clearAllFilters = (): void => {
    const newUserValue: IUserValue = { ...finalValue };

    filters?.forEach(filter => {
      if (!filter.skip) {
        let value: string | (string | number)[] = '';

        if (filter.multiple) {
          // normalize defaultValue into an array when multiple is true
          if (filter.defaultValue !== undefined && filter.defaultValue !== null) {
            value = Array.isArray(filter.defaultValue)
              ? (filter.defaultValue as (string | number)[])
              : [filter.defaultValue as string | number];
          } else {
            value = [];
          }
        }

        if (filter.must) {
          // for single-value required filters, coerce numeric default to string
          if (filter.defaultValue !== undefined && filter.defaultValue !== null) {
            value =
              typeof filter.defaultValue === 'number'
                ? String(filter.defaultValue)
                : (filter.defaultValue as string);
          } else {
            value = '';
          }
        }

        newUserValue[filter.name] = {
          value,
          type: filterTypes.is,
          options: filter.options,
          filterType: filter.type,
          typeQuery: filter.typeQuery,
          skip: !!filter.skip,
        };
      }
    });

    setUserValue(newUserValue);
    setFinalvalue(newUserValue);
  };

  const numberOfFilters = useMemo(() => {
    let totalFilters = 0;
    Object.keys(finalValue || {}).forEach(key => {
      if (!finalValue[key].skip && Array.isArray(finalValue[key].value || '')) {
        if ((finalValue[key].value as (string | number)[]).length > 0) {
          totalFilters += 1;
        }
      } else if (finalValue[key].value && !finalValue[key].skip) {
        totalFilters += 1;
      }
    });
    return totalFilters;
  }, [finalValue]);

  return (
    <>
      {open ? (
        <>
          {activeIndex === -1 ? (
            <SelectionContainer ref={ref}>
              <div className="p-3 d-flex justify-content-between">
                <h4 className="m-0">Filter By</h4>
                <MuteText onClick={() => clearAllFilters()} className="pointer">
                  Clear Filters
                </MuteText>
              </div>
              {filters?.map((filter, i) => (
                <>
                  {filter.skip ? (
                    <></>
                  ) : (
                    <>
                      <Border key={`${filter.name}-border`} />
                      <Filter
                        userValue={userValue}
                        key={filter.name}
                        item={filter}
                        setActive={() => setActiveIndex(i)}
                      />
                    </>
                  )}
                </>
              ))}
            </SelectionContainer>
          ) : (
            filters?.map((filter, i) => (
              <ActiveFilter
                key={filter.name}
                applyFilter={applyFilter}
                clearFilter={() => clearFilter(filter)}
                close={closeSelection}
                closeDropdown={closeDropdown}
                filter={userValue[filter.name] || {}}
                filterOptions={filterOptions}
                index={i}
                isActive={i === activeIndex}
                item={filter}
                updateMultiple={obj => updateMultiple(filter.name, obj)}
              />
            ))
          )}
        </>
      ) : titleBtn ? (
        <FilterTitleButton
          className={`flex-btn ${btnClassName || 'light-btn'}`}
          loading={loading}
          onClick={openDropdown}
          size="mini"
        >
          Filters
          {numberOfFilters ? (
            <>
              <MuteText className="mx-1">({numberOfFilters})</MuteText>
              <CancelButton onClick={() => clearAllFilters()} size={12} />
            </>
          ) : (
            <></>
          )}
        </FilterTitleButton>
      ) : (
        <FilterHoverButton className="flex-btn" loading={loading} onClick={openDropdown}>
          Filters
          {numberOfFilters ? (
            <>
              <MuteText>&nbsp; ({numberOfFilters})&nbsp;&nbsp;</MuteText>
              <CancelButton onClick={() => clearAllFilters()} size={16} />
            </>
          ) : (
            <></>
          )}
        </FilterHoverButton>
      )}
    </>
  );
}

export default FilterButton;

const Option = styled.div`
  border-radius: 5px;
  max-width: 100%;
  overflow: hidden;
  &:hover {
    background: ${({ theme }) => theme.colors.box};
  }
  &.active {
    background: ${({ theme }) => theme.colors.box};
    span {
      font-weight: bold;
    }
  }
  .hide {
    opacity: 0;
  }
  .show {
    opacity: 1;
  }
`;

function Filter({ item, setActive, userValue }) {
  const selectedFilters = useMemo(() => {
    if (item.multiple) {
      return userValue[item.name]?.toShow?.length;
    }
    if (userValue[item.name]?.value) {
      return 1;
    }
    return 0;
  }, [userValue, item]);

  return (
    <Option
      onClick={() => setActive()}
      className="pointer m-2 p-2 d-flex justify-content-between"
    >
      <div className="d-flex align-items-center">
        {isValidElement(item.icon) &&
          cloneElement(item.icon, {
            className: 'mr-2',
            size: 20,
          })}
        <span>{item.label}</span>
      </div>
      {selectedFilters ? <MuteText>{selectedFilters}</MuteText> : <></>}
    </Option>
  );
}

const ActiveFilterContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  min-width: 250px;
  box-shadow: 0px 5px 25px rgba(57, 57, 72, 0.05);
  border-radius: 5px;
  .pointer {
    cursor: pointer;
  }
`;

const DropdownIcon = styled(CaretDown)`
  margin-bottom: -3px;
  margin-left: 3px;
`;

const FilterHeader = styled.div`
  font-size: 15px;
`;

const OptionsContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
  max-width: 300px;
`;

const InputContainer = styled.div`
  input {
    outline: none;
    border: none;
    flex: 1;
    max-width: 100%;
    margin: 7px;
    min-width: 0px;
  }

  &.double {
    max-width: 45%;
  }

  &:focus-within {
    border-color: black !important;
  }
  border: 1px solid ${({ theme }) => theme.colors.grey};
  border-radius: 4px;
  display: flex;
  margin-bottom: 5px;
  max-width: 300px;
  flex-wrap: wrap;
  flex: 1;
`;

const InputsContainer = styled.div`
  max-width: 300px;
  display: flex;
  column-gap: 5px;
  align-items: center;
  justify-content: space-between;
`;

interface IFilterOption {
  value: string | number;
  text: string;
  content?: ReactNode;
  image?: {
    src: string;
  };
  flag?: string;
  item?: {
    region?: string;
  };
}

interface IFilterTypeOption {
  key: string;
  value: string;
  text: string;
}

interface IFilterItem {
  label?: string;
  type?: 'text' | 'date' | 'select';
  multiple?: boolean;
  numeric?: boolean;
  placeholder?: string;
  prefix?: ReactElement;
  custom_options?: IFilterTypeOption[];
  options?: IFilterOption[];
  customSearch?: (option: IFilterOption, search: string) => boolean;
  search?: boolean;
  sorted?: boolean;
}

interface IFilter {
  value?: string | number | (string | number)[] | Date | [Date | null, Date | null];
  toShow?: string | string[] | ReactElement;
  type?: string;
  index?: number;
}

interface IUpdateMultipleParams {
  value?: string | number | (string | number)[] | Date | [Date | null, Date | null];
  toShow?: string | string[] | ReactElement;
  type?: string;
  index: number;
}

interface IActiveFilterProps {
  item: IFilterItem;
  updateMultiple: (params: IUpdateMultipleParams) => void;
  applyFilter: () => void;
  filterOptions: IFilterTypeOption[];
  isActive: boolean;
  close: () => void;
  filter: IFilter;
  closeDropdown: () => void;
  clearFilter: () => void;
  index: number;
}

type IOptionValueType =
  | string
  | number
  | (string | number)[]
  | Date
  | [Date | null, Date | null];

function ActiveFilter({
  item,
  updateMultiple,
  applyFilter,
  filterOptions,
  isActive,
  close,
  filter,
  closeDropdown,
  clearFilter,
  index,
}: IActiveFilterProps): JSX.Element {
  const [filteredOptions, setFilteredOptions] = useState<IFilterOption[]>([]);

  const isOptionActive = (optionValue: string | number): boolean => {
    if (item.multiple) {
      return ((filter.value as (string | number)[]) || []).includes(optionValue);
    }
    return optionValue === filter.value;
  };

  const updateType = (value: string): void => {
    updateMultiple({
      value: value === 'BETWEEN' ? ['', ''] : '',
      toShow: '',
      type: value,
      index,
    });
  };

  const [search, setSearch] = useState<string>('');

  const ref = useRef<HTMLDivElement>(null);
  useOutsideAlerter(ref, closeDropdown);

  const setOption = (option: {
    value: IOptionValueType;
    text: string | ReactElement;
  }): void => {
    const { value, text } = option;
    if (item.multiple) {
      const prevValue = (filter.value as (string | number)[]) || [];
      const prevShow = (filter.toShow as string[]) || [];

      if (prevValue.includes(value as string | number)) {
        const toSet = prevValue.filter(x => x !== value);
        const toSetText = prevShow.filter(x => x !== text);
        updateMultiple({
          value: toSet,
          toShow: toSetText,
          index,
        });
      } else if (value === -1 || !value) {
        updateMultiple({
          value: [value as string | number],
          toShow: [text as string],
          index,
        });
      } else {
        updateMultiple({
          value: [...prevValue, value as string | number],
          toShow: [...prevShow, text as string],
          index,
        });
      }
    } else if (filter?.value?.toString().includes(value.toString())) {
      updateMultiple({
        value: '',
        toShow: '',
        index,
      });
    } else {
      updateMultiple({
        value,
        toShow: text,
        index,
      });
    }
  };

  useEffect(() => {
    if (search) {
      let arr: IFilterOption[] = [];
      if (CommonUtility.isFunction(item.customSearch)) {
        arr = item.options?.filter(x => item.customSearch!(x, search)) || [];
      } else {
        arr =
          item.options?.filter(x =>
            x.text.toLowerCase()?.includes(search.toLowerCase()),
          ) || [];
      }
      arr = arr.sort((option1, option2) => {
        const re = new RegExp(`^${search}`, 'i');
        return re.test(option1.value.toString())
          ? re.test(option2.value.toString())
            ? option1.value.toString().localeCompare(option2.value.toString())
            : -1
          : 1;
      });
      setFilteredOptions(arr);
    } else {
      setFilteredOptions(item.options || []);
    }
  }, [search, item.customSearch, item.options]);

  const isBetween = useMemo(() => filter.type === 'BETWEEN', [filter.type]);

  if (!isActive) return <></>;

  if (item.type === 'text') {
    return (
      <ActiveFilterContainer className="p-3" ref={ref}>
        <div className="d-flex align-items-center justify-content-between">
          <FilterHeader className="d-flex align-items-center">
            <MuteText>{item.label}</MuteText>
            <Dropdown
              className="ml-2"
              options={item.custom_options || filterOptions}
              inline
              value={filter.type}
              icon={<DropdownIcon />}
              onChange={(e, { value }) => updateType(value as string)}
            />
          </FilterHeader>
          <MuteText className="pointer" onClick={() => clearFilter()}>
            Clear Filter
          </MuteText>
        </div>
        <Border className="my-3" />
        <InputsContainer>
          <InputContainer className={isBetween ? 'double' : ''}>
            {isValidElement(item.prefix) && cloneElement(item.prefix)}
            {isBetween || item.numeric ? (
              <NumberMaskInput
                maxValue={9999999999}
                placeholder={
                  isBetween
                    ? 'From'
                    : item.placeholder
                      ? item.placeholder
                      : `Search for ${item.label}`
                }
                value={
                  isBetween ? (filter.value as string[])[0] : (filter.value as string)
                }
                onChange={(e: string) =>
                  setOption({
                    value: isBetween ? [e, (filter.value as string[])[1]] : e,
                    text: isBetween ? (
                      <span className="d-flex align-items-center">
                        {e} <ArrowsHorizontal className="mx-1" size={10} />{' '}
                        {(filter.value as string[])[1]}
                      </span>
                    ) : (
                      e
                    ),
                  })
                }
              />
            ) : (
              <input
                placeholder={`Search for ${item.label}`}
                value={filter.value as string}
                onChange={e =>
                  setOption({
                    value: e.target.value,
                    text: e.target.value,
                  })
                }
              />
            )}
          </InputContainer>
          {isBetween && (
            <>
              <span>-</span>
              <InputContainer className="double">
                {isValidElement(item.prefix) && cloneElement(item.prefix)}
                <NumberMaskInput
                  placeholder="To"
                  value={(filter.value as string[])[1]}
                  onChange={(e: string) =>
                    setOption({
                      value: [(filter.value as string[])[0], e],
                      text: (
                        <span className="d-flex align-items-center">
                          {(filter.value as string[])[0]}{' '}
                          <ArrowsHorizontal className="mx-1" size={10} /> {e}
                        </span>
                      ),
                    })
                  }
                />
              </InputContainer>
            </>
          )}
        </InputsContainer>
        <div className="d-flex w-100 mt-4">
          <StyledButton
            className="flex-grow-1"
            onClick={() => {
              applyFilter();
            }}
          >
            Apply
          </StyledButton>
          <HoverBorderButton onClick={() => close()} className="flex-grow-1">
            Cancel
          </HoverBorderButton>
        </div>
      </ActiveFilterContainer>
    );
  }

  if (item.type === 'date') {
    const dateParams = {};
    if (isBetween) {
      dateParams['selectsRange'] = true;
    }
    return (
      <ActiveFilterContainer className="p-3" ref={ref}>
        <div className="d-flex align-items-center justify-content-between">
          <FilterHeader className="d-flex align-items-center">
            <MuteText>{item.label}</MuteText>
            <Dropdown
              className="ml-2"
              options={item.custom_options || filterOptions}
              inline
              value={filter.type}
              icon={<DropdownIcon />}
              onChange={(e, { value }) => updateType(value as string)}
            />
          </FilterHeader>
          <MuteText className="pointer" onClick={() => clearFilter()}>
            Clear Filter
          </MuteText>
        </div>
        <Border className="my-3" />
        <DatepickerContainer>
          {isBetween && (
            <DateRangeInputContainer>
              <CustomDateInput label="Start" date={(filter.value as [Date, Date])[0]} />
              <CustomDateInput label="End" date={(filter.value as [Date, Date])[1]} />
            </DateRangeInputContainer>
          )}
          <ReactDatePicker
            startOpen
            inline
            {...dateParams}
            startDate={
              isBetween
                ? (filter.value as [Date, Date])[0] &&
                  new Date((filter.value as [Date, Date])[0])
                : (filter.value as Date)
            }
            endDate={
              isBetween
                ? (filter.value as [Date, Date])[1] &&
                  new Date((filter.value as [Date, Date])[1])
                : (filter.value as Date)
            }
            onChange={(update: Date | [Date | null, Date | null]) => {
              setOption({
                value: update,
                text: isBetween ? (
                  <span className="d-flex align-items-center">
                    {DateUtility.formatDate(
                      (update as [Date, Date])[0],
                      DateFormat.yearMonthDate,
                    )}
                    <ArrowsHorizontal className="mx-1" size={10} />
                    {DateUtility.formatDate(
                      (update as [Date, Date])[1],
                      DateFormat.yearMonthDate,
                    )}
                  </span>
                ) : (
                  DateUtility.formatDate(update as Date, DateFormat.yearMonthDate)
                ),
              });
            }}
          />
        </DatepickerContainer>
        <div className="d-flex w-100 mt-4">
          <StyledButton
            className="flex-grow-1"
            onClick={() => {
              applyFilter();
            }}
          >
            Apply
          </StyledButton>
          <HoverBorderButton onClick={() => close()} className="flex-grow-1">
            Cancel
          </HoverBorderButton>
        </div>
      </ActiveFilterContainer>
    );
  }

  const sortedOptions = useMemo(
    () =>
      item.sorted === false
        ? filteredOptions
        : CommonUtility.sortDropdownOptions(filteredOptions),
    [filteredOptions, item.sorted],
  );

  return (
    <ActiveFilterContainer className="p-3" ref={ref}>
      <div className="d-flex align-items-center justify-content-between">
        <FilterHeader className="d-flex align-items-center">
          <MuteText>{item.label}</MuteText>
          <Dropdown
            className="ml-2"
            options={item.custom_options || filterOptions}
            inline
            value={filter.type}
            icon={<DropdownIcon />}
            onChange={(e, { value }) => updateType(value as string)}
          />
        </FilterHeader>
        <MuteText className="pointer" onClick={() => clearFilter()}>
          Clear Filter
        </MuteText>
      </div>
      <Border className="my-3" />
      {(item.search || item.multiple) && (
        <InputContainer>
          <input
            className={`w-100 ${
              ((filter.value as (string | number)[]) || []).length !== 0 && item.multiple
                ? 'small'
                : ''
            }`}
            placeholder="Search for one or more options..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </InputContainer>
      )}
      {sortedOptions.length > 0 ? (
        <OptionsContainer>
          {sortedOptions.map(option => (
            <OptionRenderer
              key={option.value}
              option={option}
              countryFlag={option.flag || option.item?.region}
              filter={filter}
              isOptionActive={isOptionActive}
              setOption={(opt: IFilterOption) =>
                setOption({ value: opt.value, text: opt.text })
              }
            />
          ))}
        </OptionsContainer>
      ) : (
        'No Options found'
      )}
      <div className="d-flex w-100 mt-4">
        <StyledButton
          className="flex-grow-1"
          onClick={() => {
            applyFilter();
          }}
        >
          Apply
        </StyledButton>
        <HoverBorderButton onClick={() => close()} className="flex-grow-1">
          Cancel
        </HoverBorderButton>
      </div>
    </ActiveFilterContainer>
  );
}

const StyledImage = styled.img`
  width: 25px;
  height: 25px;
  object-fit: contain;
`;

interface IOptionImage {
  src: string;
  alt?: string;
}

interface IOption {
  value: string | number;
  text: string;
  content?: ReactNode;
  image?: IOptionImage;
}

interface IOptionRendererProps {
  isOptionActive: (value: string | number) => boolean;
  option: IOption;
  filter: any; // Replace 'any' with your actual filter type
  setOption: (option: IOption) => void;
  countryFlag?: string;
}

function OptionRenderer({
  isOptionActive,
  option,
  filter,
  setOption,
  countryFlag,
}: IOptionRendererProps): JSX.Element {
  const active = useMemo(
    () => isOptionActive(option.value),
    [isOptionActive, option.value, filter],
  );

  return (
    <Option
      onClick={() => setOption(option)}
      className={`d-flex justify-content-between pointer m-2 p-2 align-items-center ${
        active ? 'active' : ''
      }`}
    >
      {isValidElement(option.content) ? (
        cloneElement(option.content)
      ) : (
        <div className="d-flex align-items-center">
          {countryFlag && <Flag name={countryFlag} />}
          {option?.image?.src && <StyledImage className="mr-2" src={option.image.src} />}
          <span>{option.text}</span>
        </div>
      )}
      <Check className={active ? 'show' : 'hide'} size={18} />
    </Option>
  );
}

const Pill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  padding: 0px 7px;
  font-size: 10px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey};
  .pointer {
    cursor: pointer;
  }

  .text-container {
    max-width: 300px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-top: 2px;
  }

  .label {
    margin-top: 2px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.box};
  }
`;

const CrossButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  margin-left: 2px;
  border-radius: 50%;
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

interface IFilterOption {
  value: string | number;
  conditionImage?: ReactElement;
}

interface IFilterData {
  name: string;
  label?: string;
  icon?: ReactElement;
  custom_options?: IFilterOption[];
}

interface IFilterPillProps {
  data: IFilterData;
  userValue: IUserValue;
  removeFilter: (name: string) => void;
  openRelevantFilter: (index: number) => void;
}

export function FilterPill({
  data,
  userValue,
  removeFilter,
  openRelevantFilter,
}: IFilterPillProps): JSX.Element {
  const toShow = userValue[data.name]?.toShow;

  const text = useMemo(() => {
    if (Array.isArray(toShow)) {
      if (toShow.length > 0) {
        if (toShow.length > 2) {
          return `${toShow.slice(0, 2).join(',')}...`;
        }
        return toShow.join(',');
      }
      return '';
    }
    return toShow;
  }, [toShow]);

  const index = useMemo(() => userValue[data.name]?.index, [data.name, userValue]);

  const imageToRender = useMemo(() => {
    if (data.custom_options) {
      const selectedOption = data.custom_options.find(
        x => x.value === userValue[data.name]?.type,
      );
      if (selectedOption) {
        return selectedOption.conditionImage;
      }
    } else {
      const selectedOption = filterOptions.find(
        x => x.value === userValue[data.name]?.type,
      );
      if (selectedOption) {
        return selectedOption.conditionImage;
      }
    }
    return null;
  }, [data, userValue]);

  if (text) {
    return (
      <Pill className="ml-2 cursor-pointer" onClick={() => openRelevantFilter(index)}>
        {isValidElement(data.icon) &&
          cloneElement(data.icon as ReactElement<IconProps>, {
            className: 'mr-2',
            size: 12,
          })}
        <MuteText className="label" onClick={() => openRelevantFilter(index)}>
          {data.label}&nbsp;
        </MuteText>
        {isValidElement(imageToRender) && cloneElement(imageToRender)}
        &nbsp;
        <span className="text-container">
          {isValidElement(text) ? cloneElement(text) : text}
        </span>
        <CrossButton
          onClick={e => {
            e.stopPropagation();
            removeFilter(data.name);
          }}
        >
          <X size={12} />
        </CrossButton>
      </Pill>
    );
  }
  return <></>;
}
